import { EntitlementCategory } from "~/graphql/generated/graphql"
import type { SectionId } from "~/shared/theme/tokens"
import type { EntitlementItem } from "./types"

/**
 * Categories the DCE graph already serves through `libraryV2`, rendered by the
 * Studies / Digital Events / Ebooks sections. Listing them again from commerce
 * would duplicate every row, so none of them appear in the sections below.
 *
 * That holds for expired rows too, which it did not always. They used to get a
 * cross-category `Access ending or ended` section of their own, because
 * `libraryV2` deletes a revoked row and so had no card to carry them. They now go
 * where the customer looks for them — an expired simulcast into Digital Events,
 * greyed and unclickable, the way myLifeway lists its own
 * (`library/orphanEntitlements.ts`). One place per item, matching the rest of the
 * page; see FINDINGS.md gap 30.
 */
const ALREADY_SERVED: readonly EntitlementCategory[] = [
  EntitlementCategory.Video,
  EntitlementCategory.Simulcast,
  EntitlementCategory.Ebook,
]

/** The commerce half's own sections, a subset of the page's. */
export type EntitlementSectionId = Extract<SectionId, "downloads" | "other-content" | "memberships" | "other">

export interface EntitlementSectionSpec {
  readonly id: EntitlementSectionId
  readonly title: string
  readonly emptyMessage: string
  readonly categories: readonly EntitlementCategory[]
}

/**
 * The sections MLC has that the graph does not — plus a catch-all.
 *
 * `Other` is not decoration. MLC composes its page as a six-way reduce over
 * `distributionAction` with no default branch, so a seat whose action it does
 * not know renders in NO section and vanishes silently. That is observed, not
 * theoretical: `distributionAction: "UNKNOWN"` exists on a real UAT account and
 * is invisible in MLC today (FINDINGS.md Finding D). Anything this client
 * cannot place lands in `Other` instead, including enum members added to the
 * schema after this build shipped.
 */
export const ENTITLEMENT_SECTIONS: readonly EntitlementSectionSpec[] = [
  {
    id: "downloads",
    title: "Downloads",
    emptyMessage: "You have no downloads.",
    categories: [EntitlementCategory.Download],
  },
  {
    id: "other-content",
    title: "All Other Content",
    emptyMessage: "You have no other content.",
    categories: [EntitlementCategory.Application],
  },
  {
    id: "memberships",
    title: "Memberships",
    emptyMessage: "You have no memberships.",
    categories: [EntitlementCategory.Membership],
  },
  {
    id: "other",
    title: "Other",
    emptyMessage: "Nothing here — which is the outcome we want.",
    categories: [EntitlementCategory.Unknown],
  },
]

/** The true fallback: anything this build cannot place lands here. */
export const OTHER_SECTION_ID: EntitlementSectionId = "other"

const sectionForCategory = new Map<EntitlementCategory, EntitlementSectionId>(
  ENTITLEMENT_SECTIONS.flatMap(section => section.categories.map(category => [category, section.id] as const)),
)

/**
 * Buckets rows by section, dropping only what another section of the page
 * already renders. Everything else is placed, and anything unplaceable goes to
 * `Other` — so a new category cannot disappear the way `UNKNOWN` does in MLC.
 *
 * A `VIDEO` / `SIMULCAST` / `EBOOK` row is dropped here whatever its state,
 * because Studies / Digital Events / Ebooks render it: live from `libraryV2` with
 * the seat's window annotated on, or rebuilt from the seat itself by
 * `library/orphanEntitlements.ts` when `libraryV2` does not return it — for a
 * lapsed row it deleted, and for an active EBOOK row it never had (FINDINGS.md
 * gap 9). Each renders the item exactly once.
 *
 * That last case is why the ebooks branch exists at all. An active `EBOOK` row
 * absent from `libraryV2` used to be dropped here on the strength of a section
 * that was not in fact showing it, so the customer's own book appeared nowhere on
 * the page.
 */
export const bucketBySection = (
  items: readonly EntitlementItem[],
): ReadonlyMap<EntitlementSectionId, readonly EntitlementItem[]> => {
  const buckets = new Map<EntitlementSectionId, EntitlementItem[]>(
    ENTITLEMENT_SECTIONS.map(section => [section.id, []]),
  )

  for (const item of items) {
    if (ALREADY_SERVED.includes(item.category)) {
      continue
    }

    const sectionId = sectionForCategory.get(item.category) ?? OTHER_SECTION_ID
    buckets.get(sectionId)?.push(item)
  }

  return buckets
}

/**
 * Every row whose category `libraryV2` also serves — the source of the view
 * window for those library cards. Returned rather than counted so the page can
 * build the match index from them.
 */
export const alreadyServedRows = (items: readonly EntitlementItem[]): readonly EntitlementItem[] =>
  items.filter(item => ALREADY_SERVED.includes(item.category))

/**
 * How many rows the commerce sections hide because a content section above
 * renders them. Surfaced as a count, for honesty — and because it is the number
 * that would expose the dedupe going wrong: a row counted here that no section
 * above actually shows is invisible on the page.
 */
export const alreadyServedCount = (items: readonly EntitlementItem[]): number => alreadyServedRows(items).length
